/**
 * FirstSalePanel (US-17-GR-45, work item 16493)
 *
 * `GET /api/admin/metrics` has always returned a populated `firstSale` block —
 * `AdminMetricsResponse.FirstSale`, built by US-17-GR-39 — and the TypeScript client never
 * declared the field, so the whole block was dropped at the type boundary and nothing rendered
 * it. Not rendered wrongly: never reached a component at all. The same class of bug this file's
 * sibling already carries a note about, where `pro` and `unknown` were missing from
 * `tierDistribution` and every Pro subscriber vanished on the way in.
 *
 * Reads the summary the page has ALREADY fetched. No extra request: `AnalyticsPage` calls
 * `getAdminMetrics()` once for its headline strip, and this block arrives in that same response.
 *
 * ## Two fields are nullable on purpose, and must never render as 0
 *
 * The C# DTO is explicit about both, and the wording below is taken from its own XML docs rather
 * than invented here:
 *
 *   - `averageDaysToFirstSale` — null while there is no registration-date integration with
 *     Profile.API. "Frontend must render 'Not yet measured' rather than a numeric value."
 *   - `averageRevenueAtFirstSale` — null because `Card` carries no revenue column at all, so
 *     there is no honest way to compute it. "Frontend must render 'Not tracked'."
 *
 * A 0 in either place would read as a measurement, which is exactly the false-green shape
 * US-17-GR-34 fixed for revenue and NPS. `NotMeasured` renders the phrase in a visibly different
 * style from a figure, so the two cannot be confused at a glance.
 *
 * ## The anomaly rate needs its denominator
 *
 * `anomalyRatePercent` is 0 when `marksInTrailing30Days` is 0, and the DTO says so: "check
 * MarksInTrailing30Days to distinguish a genuine 0% from an unmeasured 0%". The rate is therefore
 * never shown on its own — the mark count it is computed over is always beside it.
 */

import { TrendingUp } from 'lucide-react';
import type { FirstSaleMetrics } from '../services/adminMetricsService';
import { ScopeNote } from './ScopeNote';

/** The exact phrases the C# DTO's own documentation prescribes for its two nullable fields. */
export const NOT_YET_MEASURED = 'Not yet measured';
export const NOT_TRACKED = 'Not tracked';

const ANOMALY_SCOPE_EXPLANATION =
  'Flagged marks as a percentage of confirmed marks in the fixed trailing 30 days. The rate is 0 ' +
  'when there are no marks at all, so it is always shown with the mark count it was computed ' +
  'over — 0% of 0 marks is not the same claim as 0% of 400.';

/** A measured figure. Deliberately not the sibling panel's KpiCard: this one has a sibling below
 *  that renders a PHRASE, and the two must be visually distinguishable. */
function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * A field that has no value yet. Same slot and same label as `Stat`, but the value is a phrase in
 * a muted, italic, smaller style — so "Not yet measured" cannot be mistaken for a figure, and the
 * field is still visibly present rather than silently omitted.
 */
function NotMeasured({ label, phrase, why }: { label: string; phrase: string; why: string }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4 border border-dashed border-zinc-700">
      <p className="text-sm italic text-zinc-500">{phrase}</p>
      <p className="text-xs text-zinc-400 mt-1">
        {label}
        <ScopeNote explanation={why} label="Why" className="ml-1.5" />
      </p>
    </div>
  );
}

export function FirstSalePanel({ metrics }: { metrics: FirstSaleMetrics }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <TrendingUp className="w-4 h-4 text-emerald-400" aria-hidden="true" />
        <h3 className="text-sm font-medium text-zinc-200">First Sale</h3>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Users with a first sale"
          value={metrics.usersAchievedFirstSale.toLocaleString()}
          sub={`of ${metrics.usersWithin90Days.toLocaleString()} in window`}
        />
        <Stat
          label="First-sale rate"
          value={`${metrics.firstSaleRate.toFixed(1)}%`}
          sub={`target ${metrics.targetRate.toFixed(1)}%${metrics.onTarget ? ' — on target' : ''}`}
        />

        {metrics.averageDaysToFirstSale == null ? (
          <NotMeasured
            label="Avg days to first sale"
            phrase={NOT_YET_MEASURED}
            why="There is no registration-date integration with Profile.API yet, so this cannot be computed. It is not zero days."
          />
        ) : (
          <Stat
            label="Avg days to first sale"
            value={metrics.averageDaysToFirstSale.toFixed(1)}
          />
        )}

        {metrics.averageRevenueAtFirstSale == null ? (
          <NotMeasured
            label="Avg revenue at first sale"
            phrase={NOT_TRACKED}
            why="No column records the sale amount, so there is no honest way to compute this from the first-sale signal. It is not zero revenue."
          />
        ) : (
          <Stat
            label="Avg revenue at first sale"
            value={new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(metrics.averageRevenueAtFirstSale)}
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat
          label="Confirmed marks (window)"
          value={metrics.confirmedMarksInWindow.toLocaleString()}
          sub={`${metrics.totalConfirmedMarksLifetime.toLocaleString()} lifetime`}
        />
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <p className="text-2xl font-semibold text-zinc-100">
            {metrics.anomalyRatePercent.toFixed(1)}%
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            Anomaly rate (30d)
            <ScopeNote explanation={ANOMALY_SCOPE_EXPLANATION} label="Why" className="ml-1.5" />
          </p>
          <p className="text-xs text-zinc-600 mt-0.5">
            {metrics.flaggedInTrailing30Days.toLocaleString()} flagged of{' '}
            {metrics.marksInTrailing30Days.toLocaleString()} marks
          </p>
        </div>
        <Stat
          label="Trust-scope conditions"
          value={
            metrics.anomalyRateExceedsThreshold && metrics.confirmedMarksMeetsThreshold
              ? 'Both met'
              : metrics.anomalyRateExceedsThreshold || metrics.confirmedMarksMeetsThreshold
                ? 'One met'
                : 'Neither met'
          }
          sub={`rate > ${metrics.anomalyRateThresholdPercent.toFixed(1)}% and marks >= ${metrics.confirmedMarksThreshold.toLocaleString()}`}
        />
      </div>
    </div>
  );
}

export default FirstSalePanel;
